import type { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { environment } from '@env/environment';
import { PAGE_ROUTE } from './constants/page-route';
import { ErrorPageType } from './enums/error-page-type';
import { AppClaimsGuard } from './guards/app-claims-guard';
import { errorPageGuard } from './guards/error-page-guard';
import { publicErrorPageGuard } from './guards/public-error-page-guard';
import { supplyChainUserRouteGuard } from './guards/supply-chain-user-guard';
import { ClientDefaultComponent } from './modules/client-management/pages/client-default/client-default.component';
import { AccessRoleType } from './modules/user-management/enums/access-role-type.enum';
import { AuthorizedShellComponent } from './pages/authorized-shell/authorized-shell-component';
import { ClientManagementShellComponent } from './pages/client-management-shell/client-management-shell.component';
import { MainShellComponent } from './pages/main-shell/main-shell.component';
import { UserManagementShellComponent } from './pages/user-management-shell/user-management-shell.component';
import { TODFactory } from './tod-factory';
import { RCPUrlGuard } from './guards/rcp-url-guard';
import { ServiceLineType } from './enums/service-type.enum';

export const routes: Routes = [
    {
        path: PAGE_ROUTE.Main,
        component: AuthorizedShellComponent,
        providers: [TODFactory()],
        children: [
            {
                path: PAGE_ROUTE.UserManagement,
                component: UserManagementShellComponent,
                canActivate: [MsalGuard, AppClaimsGuard],
                loadChildren: () =>
                    import('./modules/user-management/user-management.routes').then(
                        (m) => m.UserManagementRoutes,
                    ),
            },
            {
                path: PAGE_ROUTE.ClientManagement,
                canActivate: [MsalGuard, AppClaimsGuard],
                children: [
                    {
                        path: '',
                        component: ClientDefaultComponent,
                        canActivate: [supplyChainUserRouteGuard],
                        data: {
                            roles: [
                                environment.accessRoles[AccessRoleType.CompanyAdmin],
                                environment.accessRoles[
                                    AccessRoleType.CompanyPrimaryAdmin
                                ],
                            ],
                            serviceLines: [
                                environment.serviceLines[ServiceLineType.Testing],
                                environment.serviceLines[ServiceLineType.Inspection],
                            ],
                        },
                        pathMatch: 'full',
                    },
                    {
                        path: ':id',
                        component: ClientManagementShellComponent,
                        loadChildren: () =>
                            import(
                                './modules/client-management/client-management.routes'
                            ).then((m) => m.ClientManagementRoutes),
                    },
                ],
            },

            {
                path: PAGE_ROUTE.DashBoard,
                canActivate: [MsalGuard],
                loadComponent: () =>
                    import(
                        './modules/dashboard/pages/dashboard/dashboard.component'
                    ).then((m) => m.DashboardComponent),
            },
            {
                path: PAGE_ROUTE.Callback,
                canActivate: [RCPUrlGuard],
                loadComponent: () =>
                    import('./modules/callback/pages/callback/callback.component').then(
                        (m) => m.CallbackComponent,
                    ),
            },
            {
                path: `${PAGE_ROUTE.Error}/:id`,
                canActivate: [MsalGuard, errorPageGuard],
                loadComponent: () =>
                    import('./modules/error/pages/error/error.component').then(
                        (m) => m.ErrorComponent,
                    ),
            },
            {
                path: PAGE_ROUTE.Home,
                canActivate: [MsalGuard],
                loadComponent: () =>
                    import('./modules/home/pages/home/home.component').then(
                        (m) => m.HomeComponent,
                    ),
                pathMatch: 'full',
            },
            //to be removed when ETRF is no longer used
            // {
            //     path: PAGE_ROUTE.ETRF,
            //     canActivate: [MsalGuard, AppClaimsGuard],
            //     loadChildren: () =>
            //         import('./modules/etrf/etrf.routes').then((m) => m.ETRFRoutes),
            // },
            {
                path: PAGE_ROUTE.Inspection,
                canActivate: [MsalGuard, AppClaimsGuard],
                loadChildren: () =>
                    import('./modules/inspection/inspection.routes').then(
                        (m) => m.InspectionRoutes,
                    ),
            },
            {
                path: PAGE_ROUTE.Testing,
                canActivate: [MsalGuard, AppClaimsGuard],
                loadChildren: () =>
                    import('./modules/testing/testing.routes').then(
                        (m) => m.TestingRoutes,
                    ),
            },
            {
                path: '',
                redirectTo: PAGE_ROUTE.Callback,
                pathMatch: 'full',
            },
            {
                path: '**',
                redirectTo: `${PAGE_ROUTE.Error}/${ErrorPageType.NotFound}`,
            },
        ],
    },
    {
        path: PAGE_ROUTE.TermsOfUse,
        component: AuthorizedShellComponent,
        canActivate: [MsalGuard],
        providers: [TODFactory()],
        loadChildren: () =>
            import('./modules/terms-of-use/terms-of-use.routes').then(
                (m) => m.TermsRoutes,
            ),
        pathMatch: 'full',
    },
    {
        path: PAGE_ROUTE.MainPublic,
        component: MainShellComponent,
        providers: [TODFactory()],
        children: [
            {
                path: PAGE_ROUTE.ClientSelfOnBoarding,
                loadChildren: () =>
                    import(
                        './modules/client-self-onboarding/client-self-onboarding.routes'
                    ).then((m) => m.ClientSelfOnboardingRoutes),
            },
            {
                path: `${PAGE_ROUTE.Error}/:id`,
                canActivate: [publicErrorPageGuard],
                loadComponent: () =>
                    import('./modules/error/pages/error/error.component').then(
                        (m) => m.ErrorComponent,
                    ),
            },
            {
                path: '**',
                redirectTo: `${PAGE_ROUTE.Error}/${ErrorPageType.NotFound}`,
            },
        ],
    },
    {
        path: '',
        redirectTo: PAGE_ROUTE.Main,
        pathMatch: 'full',
    },
    {
        path: '**',
        redirectTo: `${PAGE_ROUTE.MainPublic}/${PAGE_ROUTE.Error}/${ErrorPageType.NotFound}`,
    },
];
